import {
  ArdoqId,
  APIViewpointAttributes,
  CreateViewpointResponse,
  CreateViewpointAttributes,
  AssetType,
  APILoadTraversalResponse,
  APILoadTraversalParams,
} from '@ardoq/api-types';
import { ApiResponse } from './types';
import {
  Method,
  crudApi,
  fetchJson,
  fetchVoidAndRespondWithId,
  renameEntity,
  toRequestOptions,
} from './api';
import {
  APICountInstancesResponse,
  ApiCountInstancesRequest,
} from '@ardoq/api-types';
import { omit } from 'lodash';
import { ardoqId, isArdoqError } from '@ardoq/common-helpers';
import { fileAssetInFolder } from './fileAssetInFolder';

const loadTraversal = (
  loadTraversalRequest: APILoadTraversalParams,
  abortSignal?: AbortSignal
): ApiResponse<APILoadTraversalResponse> => {
  const request = new Request(
    '/api/traversal/execute',
    toRequestOptions(Method.POST, loadTraversalRequest, { signal: abortSignal })
  );
  return fetchJson<APILoadTraversalResponse>(request);
};

type Prompt = string;

export type APIInferTraversalRequest = {
  input: Prompt;
  startSet: ArdoqId[];
  clientRequestId: string;
};

const infer = (inferRequest: APIInferTraversalRequest): ApiResponse<string> => {
  const { clientRequestId, ...body } = inferRequest;

  const request = new Request(
    `/api/traversal/infer?requestId=${clientRequestId}`,
    toRequestOptions(Method.POST, body)
  );
  return fetchVoidAndRespondWithId(request, clientRequestId);
};

export type APISuggestTraversalRequest = {
  startSet: ArdoqId[];
  clientRequestId: string;
  referenceIds: ArdoqId[];
  parentChildReferenceIds: ArdoqId[];
};

const suggest = (
  suggestRequest: APISuggestTraversalRequest
): ApiResponse<string> => {
  const { clientRequestId, ...body } = suggestRequest;

  const request = new Request(
    `/api/traversal/suggest?requestId=${clientRequestId}`,
    toRequestOptions(Method.POST, body)
  );
  return fetchVoidAndRespondWithId(request, clientRequestId);
};

const countInstances = ({
  startSet,
  startQuery,
  paths,
  filters,
  startSetFilterId,
  pathMatching,
  startSetExpansion,
}: ApiCountInstancesRequest): ApiResponse<APICountInstancesResponse> => {
  const request = new Request(
    '/api/traversal/counts',
    toRequestOptions(Method.POST, {
      startSet: startQuery ? null : startSet,
      startQuery,
      paths,
      filters,
      startSetFilterId,
      pathMatching,
      startSetExpansion,
    })
  );
  return fetchJson<APICountInstancesResponse>(request);
};

const copy = (traversal: APIViewpointAttributes, newName: string) => {
  return traversalApi.create({
    name: newName,
    ...omit(traversal, [
      'name',
      '_id',
      'created',
      'createdBy',
      'createdByEmail',
      'createdByName',
      'lastModifiedBy',
      'lastModifiedByEmail',
      'lastModifiedByName',
      'lastUpdated',
      '_version',
    ]),
  });
};

const createTraversal = (
  traversal: CreateViewpointAttributes
): ApiResponse<CreateViewpointResponse> => {
  return fetchJson<CreateViewpointResponse>(
    new Request(
      '/api/traversal/create',
      toRequestOptions(Method.POST, traversal)
    )
  );
};

const createInFolder = async (
  viewpoint: CreateViewpointAttributes,
  folderId: ArdoqId
): ApiResponse<CreateViewpointResponse> => {
  const viewpointResponse = await traversalApi.createTraversal(viewpoint);
  if (isArdoqError(viewpointResponse)) {
    return viewpointResponse;
  }
  await fileAssetInFolder({
    assetId: viewpointResponse.traversal._id,
    folderId,
    assetType: AssetType.TRAVERSAL,
  });
  return viewpointResponse;
};

const ensureRuleTypeId = (
  viewpoint: APIViewpointAttributes
): APIViewpointAttributes => ({
  ...viewpoint,
  pathCollapsingRules: (viewpoint.pathCollapsingRules ?? []).map(rule => ({
    ...rule,
    referenceTypeId: rule.referenceTypeId || ardoqId(),
  })),
});

// Temporary fix to make the code work before we have the according migration
// done on the BE.
const crudApiWithRuleIdCheck = () => {
  const { fetch, fetchAll, ...rest } =
    crudApi<APIViewpointAttributes>('traversal');
  return {
    ...rest,
    fetchAll: async (): ApiResponse<APIViewpointAttributes[]> => {
      const result = await fetchAll();
      if (isArdoqError(result)) {
        return result;
      }
      return result.map(ensureRuleTypeId);
    },
    fetch: async (id: ArdoqId): ApiResponse<APIViewpointAttributes> => {
      const result = await fetch(id);
      if (isArdoqError(result)) {
        return result;
      }
      return ensureRuleTypeId(result);
    },
  };
};

export const traversalApi = {
  ...crudApiWithRuleIdCheck(),
  loadTraversal,
  infer,
  suggest,
  countInstances,
  copy,
  createTraversal,
  createInFolder,
  rename: renameEntity<APIViewpointAttributes>('traversal'),
};
